// @ts-strict-ignore
import { StringUtils } from '@shared/util/string-utils';
import { ProjectType } from '../../../model/project-type';
import { IsProjectType } from '../../../model/is-project-type';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { ArticleConstants } from '@article/constants/article-constants';
import { ProjectInfoPropertyValueDef } from '@project/components/project/constants/project-info-property-value-def';
import { ProjectStatesDef } from '@project/components/project/constants/project-states-def';
import { ProjectHistoryActionDef } from '@project/components/project/constants/project-history-action-def';
import { ProjectHeadVO, ProjectOptionDefVO, ProjectOptionVO, ProjectSaveInfoVO, ProjectVO } from '@project/model/vo/project.vo';
import { PropertyUtils } from '@article/util/property-utils';
import { UserVO } from '@user/model/vo/user.vo';
import { UserUtils } from '@user/service/user-utils';
import { ArticleCompositionVO, ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ArrayUtils } from '@shared/util/array-utils';
import { RepresentationDataVO, RepresentationVO } from '@shared/vo/representation.vo';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { LocalizedStringValueVO, StringValueInLocalesVO } from '../../article/model/vo/property.vo';
import { ArticleUtils } from '@article/util/article-utils';
import { ProjectOptionUtils } from './project-option.utils';
import { DateFormatter } from '@shared/date-formatter';
import { ProjectGroupVO } from '../../../model/vo/project-group.vo';
import { ProjectGroupUtils } from './project-group-utils';
import { InitManager } from '@shared/manager/init.manager';
import { NumberUtils } from '@shared/util/number-utils';
import { ProjectModel } from '../../../model/project.model';
import { ProjectOptionDefIds } from '@project/components/project/constants/project-option-def-ids';
import { ProjectIdentity } from '../../request/project-identity';
import { SvgIconName } from '@shared/svg-icon/svg-icon-name';
import { ConnectionAreaUsageVO, MetaElementUsageVO } from '@shared/vo/meta-element.vo';
import { ArticleCompositionUtils } from '../../../util/article-composition-utils';

export class ProjectUtils {
  public static readonly DEFAULT_INITIAL_PROJECT_VERSION_DISPLAY_STRING: string = 'A';
  public static MY_ASSIGNED_PROJECTS_GROUP_LANGUAGE_KEY: string = `myAssignedProjects.groupName.label`;

  public static isNewProject(projectHead: ProjectHeadVO): boolean {
    if (!projectHead) {
      return false;
    }
    return NumberUtils.isNotNumber(projectHead.id) || projectHead.id <= 0;
  }

  public static isProjectRequiresView3DRepresentation(project: ProjectVO): boolean {
    if (!ProjectUtils.isEmptyProject(project)) {
      for (const composition of project.compositions) {
        if (composition.articleUsages && composition.articleUsages.length > 0) {
          if (PropertyUtils.containsProperty(composition.infoProperties, ArticleUsageInfoPropertyDef.ARTICLE_POSITION_X) ) {
            return false;
          }
          for (const articleUsage of composition.articleUsages) {
            if (articleUsage.infoProperties && PropertyUtils.containsProperty(articleUsage.infoProperties, ArticleUsageInfoPropertyDef.ARTICLE_POSITION_X)) {
              return false;
            }
          }
        }
      }
    }
    return true;
  }

  public static isEmptyProject(project: ProjectVO, ...ignoreArticleIds: number[]): boolean {
    if (!project || !project.compositions || project.compositions.length === 0) {
      return true;
    }
    for (const composition of project.compositions) {
      if (composition.articleUsages && composition.articleUsages.length > 0) {
        if (!ignoreArticleIds) {
          return false;
        }

        for (const usage of composition.articleUsages) {
          if (ArrayUtils.getItemIndex(usage.articleId, ignoreArticleIds) === -1) {
            return false;
          }
        }
      }
    }
    return true;
  }

  public static findArticleUsageInProjectById(project: ProjectVO, usageId: string): ArticleUsageVO {
    if (!project || !project.compositions || project.compositions.length === 0) {
      return null;
    }

    for(const articleUsage of this.articleUsages(project)) {
      if(articleUsage.id === usageId) {
        return articleUsage;
      }
    }
    return null;
  }

  public static updateRotationInfoForNewProject(project: ProjectVO): void {
    project.infoProperties.setString("rotationInfoVersion", ProjectInfoPropertyValueDef.ROTATION_INFO_VERSION_2_0);
  }

  public static updateRotationInfoForExistingProject(project: ProjectVO): void {
    const currentVersion: string = project.infoProperties.getString("rotationInfoVersion");
    if (!currentVersion) {
      project.infoProperties.setString("rotationInfoVersion", ProjectInfoPropertyValueDef.ROTATION_INFO_VERSION_1_0)
    }
  }

  public static findFirstView3DRepresentationData(project: ProjectVO): RepresentationDataVO {
    const representation: RepresentationVO = this.findFirstView3DRepresentation(project);
    if (!representation) {
      return null;
    }
    return representation.data;
  }

  public static findFirstView3DRepresentation(project: ProjectVO): RepresentationVO {
    if (!project || ArrayUtils.isEmpty(project.representations)) {
      return null;
    }
    for (const representation of project.representations) {
      if (representation && representation.type === RepresentationVO.TYPE_VIEW3D) {
        return representation;
      }
    }
    return null;
  }

  public static findFirstView3DRepresentationUrl(project: ProjectVO): StringValueInLocalesVO {
    const representation: RepresentationVO = this.findFirstView3DRepresentation(project);
    if (!representation) {
      return null;
    }
    return representation.url;
  }

  public static hasProjectComment(project: ProjectVO): boolean {
    if (!project || (!project.projectComment && project.projectComment.localizedValues)) {
      return false;
    }
    for (const localizedStringValueVO of project.projectComment.localizedValues) {
      if (!StringUtils.isEmpty(localizedStringValueVO.stringValue)) {
        return true;
      }
    }
    return false;
  }

  public static hasInternalProjectComment(project: ProjectVO): boolean {
    if (!project || StringUtils.isEmpty(project.internalProjectComment)) {
      return false;
    }
    return true;
  }

  public static getProjectComment(project: ProjectVO): string {
    let joinedRemark: string = '';
    const remarkInLocales: StringValueInLocalesVO = project ? project.projectComment : null;
    if (remarkInLocales) {
      let i: number = 0;
      while (remarkInLocales.localizedValues && i < remarkInLocales.localizedValues.length) {
        if (
          (remarkInLocales.localizedValues[i] as LocalizedStringValueVO) &&
          !StringUtils.isEmpty((remarkInLocales.localizedValues[i] as LocalizedStringValueVO).stringValue)
        ) {
          if (joinedRemark.length > 0) {
            joinedRemark += '\n\n';
          }
          joinedRemark += (remarkInLocales.localizedValues[i] as LocalizedStringValueVO).stringValue;
        }
        i++;
      }
    }
    return joinedRemark;
  }

  public static projectSortByNameFunction(a: ProjectHeadVO, b: ProjectHeadVO, ascending: boolean = true): number {
    if (ascending) {
      return StringUtils.stringCompare(a.name, b.name);
    } else {
      return -1 * StringUtils.stringCompare(a.name, b.name);
    }
  }

  public static projectSortByArticleNumberFunction(a: ProjectHeadVO, b: ProjectHeadVO, ascending: boolean = true): number {
    const articleNumberCompareResult: number = StringUtils.stringCompare(
      a.articleNumberLong.reducedArticleNumberLong, b.articleNumberLong.reducedArticleNumberLong
    );
    if (articleNumberCompareResult === 0) {
      return StringUtils.stringCompare(a.name, b.name);
    }
    return ascending ? articleNumberCompareResult : -1 * articleNumberCompareResult;
  }

  public static projectSortByModificationDateFunction(a: ProjectHeadVO, b: ProjectHeadVO, ascending: boolean = true): number {
    if (ascending) {
      return NumberUtils.numericCompare(a.modificationDate.getTime(), b.modificationDate.getTime());
    } else {
      return -1 * NumberUtils.numericCompare(a.modificationDate.getTime(), b.modificationDate.getTime());
    }
  }

  public static projectSortByLastHistoryActionFunction(a: ProjectHeadVO, b: ProjectHeadVO, ascending: boolean = true): number {
    const lastHistoryActionCompareResult: number = NumberUtils.numericCompare(
      this.getSortingOrderByLastHistoryAction(a),
      this.getSortingOrderByLastHistoryAction(b)
    );
    if (lastHistoryActionCompareResult === 0) {
      return StringUtils.stringCompare(a.name, b.name);
    }
    return ascending ? lastHistoryActionCompareResult : -1 * lastHistoryActionCompareResult;
  }

  private static getSortingOrderByLastHistoryAction(projectHead: ProjectHeadVO): number {
    if(!(projectHead.latestProjectHistoryEntry)) return -1;
    return ProjectHistoryActionDef.PROJECT_HISTORY_ACTION_TO_SORT_ORDER_MAP.get(projectHead.latestProjectHistoryEntry.action);
  }

  public static getPathNameForProjectGroup(
    localeResourceManager: LocaleResourceManager,
    projectGroup: ProjectGroupVO,
    user: UserVO,
    includeSlashAtTheEnd: boolean
  ): string {
    if (!projectGroup) {
      return includeSlashAtTheEnd ? '/' : '';
    } else {
      let name: string = projectGroup.groupName;
      if (user && name === ProjectUtils.MY_ASSIGNED_PROJECTS_GROUP_LANGUAGE_KEY) {
        if (projectGroup.groupOwner.userId === user.id) {
          name = 'owner.myAssignedProjects.groupName.label';
        } else {
          name = 'myAssignedProjects.groupName.label';
        }
      }
      if (ProjectGroupUtils.isLocalizedProjectGroup(projectGroup)) {
        name = localeResourceManager.getValueByKey(name, {'0': projectGroup.groupOwner.username});
      }
      return this.getPathNameForProjectGroup(localeResourceManager, projectGroup.parentGroup, user, true) + name + (includeSlashAtTheEnd ? '/' : '');
    }
  }

  public static isProjectEditable(project: ProjectVO, user: UserVO): boolean {
    return project && project.projectType !== "XCOM" && UserUtils.hasEditProjectPermission(user);
  }

  public static isProjectItemListCompareAvailable(project: ProjectVO, user: UserVO, isConstruction: boolean): boolean {
    if (!project || !user || isConstruction || !user.hasPermission("LOAD_PROJECTS")) {
      return false;
    }
    if (IsProjectType.mcs(project)) {
      return user.hasPermission("VIEW_PROJECT_STRUCTURE_IN_MCS_PROJECT");
    } else if (IsProjectType.gds(project)) {
      return user.hasPermission("VIEW_PROJECT_STRUCTURE_IN_GDS_PROJECT");
    } else if (IsProjectType.mounting(project)) {
      return true;
    } else {
      return false;
    }
  }

  public static getProjectTypeByMountingRailArticle(article: ArticleVO): ProjectType {
    if (!ArticleUtils.isMountingRail(article)) {
      return null;
    }
    return this.getProjectTypeByMountingRailArticleId(article.identity);
  }

  public static setBaseArticleProperties(project: ProjectVO, article: ArticleVO): void {
   project.infoProperties.setString("mcsGdsInitArticleNo", article.articleNo);
   project.infoProperties.setInt("mcsGdsInitArticleId", article.identity);
   project.infoProperties.setInt("mcsGdsInitArticleVersion", article.version);
  }

  public static getBaseArticleNumber(project: ProjectVO | undefined): string | undefined {
    return project?.infoProperties.getString("mcsGdsInitArticleNo");
  }

  public static getBaseArticleId(project: ProjectVO): number | undefined {
    return project?.infoProperties.getInt('mcsGdsInitArticleId');
  }

  public static getProjectTypeByMountingRailArticleId(articleId: number, returnVisibleRailTypeAsFallback: boolean = true): ProjectType {
    let result: ProjectType = returnVisibleRailTypeAsFallback ? "MOUNTING_RAIL" : null;
    if (ArticleConstants.DEFAULT_SPEEDWAY_ARTICLE_ID === articleId) {
      result = "SPEEDWAY";
    } else if (ArticleConstants.DEFAULT_FREE_MOUNTING_ARTICLE_ID === articleId) {
      result = "FREE_MOUNTING";
    }
    return result;
  }

  public static getFinalizationStateIcon(project: ProjectHeadVO): SvgIconName | null {
    if (!project) {
      return null;
    }
    if (project.isLocked) {
      // in case project is locked, we show locked status icon, no matter what latest action is
      return "lock";
    }
    return this.getFinalizationStateIconByFinalizationStateAndDocumentVersion(
      project.finalizationState,
      project.documentVersion,
      !project.latestProjectHistoryEntry ? ProjectHistoryActionDef.UNDEFINED : project.latestProjectHistoryEntry.action
    );
  }

  public static isProjectFinalized(finalizationState: number): boolean {
    return finalizationState === ProjectStatesDef.FINALIZATION_STATE_FINALIZED_WITHOUT_PDM
      || finalizationState === ProjectStatesDef.FINALIZATION_STATE_PDM_EXPORT_DONE
      || finalizationState === ProjectStatesDef.FINALIZATION_STATE_LEGACY_PROJECT_EXPORT_DONE;
  }

  public static getFinalizationStateIconByFinalizationStateAndDocumentVersion(finalizationState: number, documentVersion: number, action: number): SvgIconName {
    if (action === ProjectHistoryActionDef.LOCKED_PROJECT) {
      // only for action of locking project, locked-icon is used instead of icon determined by current finalization state,
      // even for unlocked action no special/different icon is used
      return "lock";
    }
    // action otherwise not used for determining icon. The icon directly reflects the finalization state of the project alone.
    switch (finalizationState) {
      case ProjectStatesDef.FINALIZATION_STATE_NEW:
        return "user-edit";
      case ProjectStatesDef.FINALIZATION_STATE_REQUESTED:
        return "shopping-cart";
      case ProjectStatesDef.FINALIZATION_STATE_PREPARATION:
        return "user-edit";
      case ProjectStatesDef.FINALIZATION_STATE_PDM_EXPORT_DONE:
        return "flag";
      case ProjectStatesDef.FINALIZATION_STATE_LEGACY_PROJECT_EXPORT_DONE:
        return "flag";
      case ProjectStatesDef.FINALIZATION_STATE_ORDERED:
        return "shipping";
      case ProjectStatesDef.FINALIZATION_STATE_FINALIZED_WITHOUT_PDM:
        return "flag";
      default: {
        return "user-edit";
      }
    }
  }

  public static getVersionAsLetter(version: number): string {
    if (version < 0) {
      return '-';
    }
    if (version <= 7) {
      return String.fromCharCode(65 + version);
    }
    // according to norm, rev-index I ist not allowed, clashes with comment from concept though.
    if (version <= 12) {
      return String.fromCharCode(66 + version);
    }
    return String.fromCharCode(67 + version); // according to norm, rev-index O is also not allowed.
  }

  public static isProjectPrintable(project: ProjectVO): boolean {
    return (
      project &&
      (IsProjectType.mounting(project) ||
        IsProjectType.markingSystem(project) ||
        IsProjectType.xCom(project))
    );
  }

  public static setMcsGdsProjectData(project: ProjectVO, articleId: number, articleVersion: number, articleNo: string): void {
    project.infoProperties.setInt("mcsGdsInitArticleId", articleId);
    project.infoProperties.setInt("mcsGdsInitArticleVersion", articleVersion);
    project.infoProperties.setString("mcsGdsInitArticleNo", articleNo);
  }

  public static setInitialVersionDisplayString(project: ProjectVO, projectOptionDefs: ProjectOptionDefVO[]): void {
    this.setVersionDisplayString(project, projectOptionDefs, null);
  }

  /**
   * @param project
   * @param projectOptionDefs
   * @param versionDisplayString - in case null (or empty string) is provided, default value of related project option is used.
   */
  public static setVersionDisplayString(project: ProjectVO, projectOptionDefs: ProjectOptionDefVO[], versionDisplayString: string): void {
    if (!project) {
      return;
    }
    const projectVersionProjectOptionDef: ProjectOptionDefVO = ProjectOptionUtils.findProjectOptionDefByIdentifier(
      projectOptionDefs,
      ProjectOptionDefIds.PROJECT_VERSION
    );
    let projectOption: ProjectOptionVO = ProjectOptionUtils.findProjectOptionByIdentifier(project.options, ProjectOptionDefIds.PROJECT_VERSION);
    if (!projectOption) {
      projectOption = ProjectOptionUtils.convertProjectOptionDefToProjectOption(projectVersionProjectOptionDef);
      if (!projectOption) {
        // there could be theoretical case where projectOptionDef is not present for projectVersion
        // as last resort we could create dummy fallback here.
        // Currently we cannot create version in such case (static localization infos would need to be in code for this.
        return;
      }
      if (!project.options) {
        project.options = [];
      }
      project.options.push(projectOption);
    }
    if (!versionDisplayString) {
      if (!projectVersionProjectOptionDef) {
        projectOption.value = ProjectUtils.DEFAULT_INITIAL_PROJECT_VERSION_DISPLAY_STRING;
        project.currentVersionDisplayString = ProjectUtils.DEFAULT_INITIAL_PROJECT_VERSION_DISPLAY_STRING;
      } else {
        projectOption.value = projectVersionProjectOptionDef.defaultValue;
        project.currentVersionDisplayString = projectVersionProjectOptionDef.defaultValue;
      }
    } else {
      projectOption.value = versionDisplayString;
      project.currentVersionDisplayString = versionDisplayString;
    }
  }

  public static setOrUpdateProjectOption(optionName: string, createdBy: string, project: ProjectVO, projectOptionDefs: ProjectOptionDefVO[]): void {
    if (!project) {
      return;
    }
    const projectVersionProjectOptionDef: ProjectOptionDefVO = ProjectOptionUtils.findProjectOptionDefByIdentifier(projectOptionDefs, optionName);
    let projectOption: ProjectOptionVO = ProjectOptionUtils.findProjectOptionByIdentifier(project.options, optionName);
    if (!projectOption) {
      projectOption = ProjectOptionUtils.convertProjectOptionDefToProjectOption(projectVersionProjectOptionDef);
      if (!projectOption) {
        return;
      }
      if (!project.options) {
        project.options = [];
      }
      project.options.push(projectOption);
    }
    if (StringUtils.isEmpty(createdBy)) {
      projectOption.value = '';
    } else {
      projectOption.value = createdBy;
    }
  }

  public static getVersionDisplayString(project: ProjectHeadVO|ProjectVO, includeProjectSaveDate: boolean): string {
    if (!project) {
      return '';
    }


    const projectOption: ProjectOptionVO =
      project instanceof ProjectVO
        ? ProjectOptionUtils.findProjectOptionByIdentifier(project.options, ProjectOptionDefIds.PROJECT_VERSION)
        : null;
    const projectVersionDisplayString: string = (projectOption && projectOption.value) || project.currentVersionDisplayString;

    return this.getPreparedVersionDisplayString(projectVersionDisplayString, includeProjectSaveDate ? project.modificationDate : null);
  }

  public static getRequestedVersionDisplayStringFromProjectSaveInfo(projectSaveInfo: ProjectSaveInfoVO, includeProjectSaveDate: boolean) {
    if (!projectSaveInfo) {
      return '';
    }
    return this.getPreparedVersionDisplayString(
      projectSaveInfo.requestedProjectVersionDisplayString,
      includeProjectSaveDate ? projectSaveInfo.requestedProjectVersionSaveDate : null
    );
  }

  public static getHighestFinalizedVersionDisplayStringFromProjectSaveInfo(projectSaveInfo: ProjectSaveInfoVO, includeProjectSaveDate: boolean) {
    if (!projectSaveInfo) {
      return '';
    }
    return this.getPreparedVersionDisplayString(
      projectSaveInfo.highestFinalizedProjectVersionDisplayString,
      includeProjectSaveDate ? projectSaveInfo.highestFinalizedProjectVersionSaveDate : null
    );
  }

  private static getPreparedVersionDisplayString(versionDisplayString: string, versionSaveDate: Date): string {
    let preparedVersionDisplayString = versionDisplayString || '';
    if (preparedVersionDisplayString && versionSaveDate) {
      preparedVersionDisplayString += `${' (' + DateFormatter.formatDate(versionSaveDate) + ')'}`;
    }
    return preparedVersionDisplayString;
  }

  public static createNewConfigurationGroup(applicationVersion: string): ProjectVO {
    const configurationGroup = new ProjectGroupVO();
    configurationGroup.guid = StringUtils.createUuid();
    configurationGroup.id = -1;
    configurationGroup.groupType = ProjectGroupUtils.PROJECT;
    configurationGroup.groups = [];

    const configuration: ProjectVO = this.createNewProjectVO(applicationVersion);
    configuration.projectGroup = configurationGroup;
    configurationGroup.projects = [configuration];

    return configuration;
  }

  public static createNewProjectVO(applicationVersion: string): ProjectVO {
    const project: ProjectVO = new ProjectVO();
    project.id = NaN;
    project.guid = StringUtils.createUuid();
    project.representations = [];
    project.dynamicArticlesInfo = [];
    project.compositions = [];
    project.isSharedReadOnly = false;

    const articleComposition: ArticleCompositionVO = new ArticleCompositionVO();
    articleComposition.id = StringUtils.createUuid();
    articleComposition.articleUsages = [];
    articleComposition.representations = [];
    project.compositions.push(articleComposition);

    project.projectComment = new StringValueInLocalesVO();
    project.projectComment.localizedValues = [];

    ProjectUtils.updateRotationInfoForNewProject(project);
    project.infoProperties.setString("createdInConfiguratorVersion", applicationVersion);
    project.infoProperties.setString("projectOptionDataVersion", InitManager.PROJECT_OPTION_DATA_VERSION);

    return project;
  }

  public static getConfigurationGroupName(project: ProjectVO): string {
    if (!project || !project.projectGroup || !ProjectGroupUtils.isConfigurationGroup(project.projectGroup)) {
      return null;
    }
    return project.projectGroup.groupName;
  }

  public static isNewWSPImportedProject(project: ProjectVO): boolean {
    if (!project || !project.isWSPImportedProject) {
      return false;
    }
    return ProjectUtils.isNewProject(project);
  }

  public static canRemoveCodingFingerinFreeMountingOrMCS(projectModel: ProjectModel): boolean {
    if (IsProjectType.mcsGds(projectModel.currentProject)) {
      return true;
    }
    if (!IsProjectType.freeMounting(projectModel.currentProject)) {
      return false;
    }
    if (!projectModel.user.isAdmin() && !projectModel.user.isClerk()) {
      return false;
    }
    return true;
  }

  public static canRequestOffer(user: UserVO, project: ProjectVO): boolean {
    if (!user || !project || project.finalizationState == null) {
      return false;
    }
    if (ProjectUtils.isEmptyProject(project)) {
      return false;
    }
    if (project.isSharedReadOnly) {
      return false;
    }
    if (!project.isLatestVersion) {
      return false;
    }
    if (project.finalizationState < ProjectStatesDef.FINALIZATION_STATE_REQUESTED) {
      if (!user.hasPermission("SAVE_PROJECTS")) {
        return false;
      }
      return user.hasPermission("REQUEST_FINALIZE_PROJECTS");
    } else {
      return false;
    }
  }

  public static generateProjectIdentities(projects: ProjectHeadVO[]): ProjectIdentity[] {
    return projects.map((project: ProjectVO) => {
      return new ProjectIdentity({
        id: project.id,
        version: project.currentVersion,
      });
    });
  }

  public static getFirstArticleUsage(project: ProjectVO): ArticleUsageVO {
    return project.compositions[0].articleUsages[0];
  }

  static findCompositionById(project: ProjectVO, compositionId: string): ArticleCompositionVO {
    return project.compositions.find(composition => composition.id == compositionId);
  }

  static removeArticleUsage(project: ProjectVO, articleUsage: ArticleUsageVO) {
    project.compositions.forEach(composition => ArrayUtils.removeItem(articleUsage, composition.articleUsages));
  }

  static tryGetFirstArticleUsage(project: ProjectVO): ArticleUsageVO | undefined {
    if(ArrayUtils.isEmpty(project.compositions) || ArrayUtils.isEmpty(project.compositions[0].articleUsages)) {
      return undefined;
    }
    return this.getFirstArticleUsage(project);
  }

  static tryGetLastArticleUsage(project: ProjectVO): ArticleUsageVO | undefined {
    return project?.compositions?.[0]?.articleUsages?.slice(-1)?.[0];
  }

  static getFirstArticleUsages(project: ProjectVO): ArticleUsageVO[] {
    return project.compositions[0].articleUsages;
  }

  static articleUsages(project: ProjectVO): ArticleUsageVO[] {
    if(!project) {
      return [];
    }
    return project.compositions.flatMap(composition => composition.articleUsages);
  }

  public static metaElementUsages(project: ProjectVO): MetaElementUsageVO[] {
    return project.compositions.flatMap(composition => ArticleCompositionUtils.metaElementUsages(composition));
  }

  public static connectionAreaUsages(project: ProjectVO): ConnectionAreaUsageVO[] {
    const result: ConnectionAreaUsageVO[] = [];
    for (const metaElementUsage of this.metaElementUsages(project)) {
      if (metaElementUsage instanceof ConnectionAreaUsageVO) {
        result.push(metaElementUsage);
      }
    }
    return result;
  }

  public static isProjectSaved(project?: ProjectVO) {
    return !!project?.projectGroup && project.projectGroup.id !== -1 && !project.isArticleProject;
  }
}
